import { ResponseStatus } from '@revenuemarket/http';
import { convert } from '@revenuemarket/utils';
import detectPlatform, { detectBrowser } from '@revenuemarket/utils/detectPlatform';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { setCookie } from 'cookies-next';

import { getDomainForCookie } from '~apis/axios/instance';
import { createCompanyWorkspace } from '~apis/company/createCompanyWorkspace';
import getCompany from '~apis/company/getCompany';
import { getCompanyAgreements } from '~apis/company/getCompanyAgreements';
import { putRepresentativeInfo } from '~apis/company/representatives';
import { verifyCompany } from '~apis/company/verifyCompany';
import getCompanyUserDetail from '~apis/companyuser/getCompanyUserDetail';
import getSearchCompany from '~apis/getSearchCompany';
import getAllMembers from '~apis/member/getAll';
import { getMetaData } from '~apis/metaData';
import resendCeoCreditAgreements from '~apis/trade/resendCeoCreditAgreements';
import { AuthQueryKey } from '~hooks/queries/auth/query';
import { UserCompaniesQueryKey } from '~hooks/queries/users/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { CompanyModel } from '~models/company';
import { mixpanel } from '~pages/_app';
import { deserializeAccessToken } from '~utils/auth';
import getTargetCompany from '~utils/auth/getTargetCompany';

import { USE_CEO_CREDIT_REQUEST_QUERY } from '../useCeoCreditRequestQuery';

interface CompanyIdentifier {
  companyId: string;
}

interface MemberIdentifier extends CompanyIdentifier {
  memberId: string;
}

export const CompaniesQueryKey = {
  default: () => ['companies'],
  agreements: () => [...CompaniesQueryKey.default(), 'agreements'],
  listForWorkspace: (request: CompanyModel.FindWorkspaceCompanyRequest) => [
    ...CompaniesQueryKey.default(),
    'list',
    request,
  ],
  company: ({ companyId }: CompanyIdentifier) => [
    ...CompaniesQueryKey.default(),
    'company',
    companyId,
  ],
  members: (identifier: CompanyIdentifier) => [...CompaniesQueryKey.company(identifier), 'members'],
  member: (identifier: MemberIdentifier) => [
    ...CompaniesQueryKey.members(identifier),
    'member',
    identifier.memberId,
  ],
};

export function useCompany(identifier: CompanyIdentifier) {
  return useQuery({
    queryKey: CompaniesQueryKey.company(identifier),
    queryFn: () => getCompany(identifier),
    enabled: !!identifier.companyId,
  });
}

export function useCompanyMembers(identifier: CompanyIdentifier) {
  return useQuery({
    queryKey: CompaniesQueryKey.members(identifier),
    queryFn: () => getAllMembers(identifier),
    enabled: !!identifier.companyId,
  });
}

export function useCompanyMember(identifier: MemberIdentifier) {
  return useQuery({
    queryKey: CompaniesQueryKey.member(identifier),
    queryFn: () => getCompanyUserDetail(identifier),
    enabled: !!identifier.companyId && !!identifier.memberId,
  });
}

export function useCompanyAgreements() {
  return useQuery({
    queryKey: CompaniesQueryKey.agreements(),
    queryFn: () => getCompanyAgreements(),
  });
}

export function useVerifyCompany() {
  return useMutation({
    mutationFn: verifyCompany,
  });
}

export function useCreateCompanyWorkspace() {
  const queryClient = useQueryClient();
  const { setCompanyId } = useCompanyId();
  return useMutation({
    mutationFn: createCompanyWorkspace,
    async onSuccess(response) {
      if (response.Status === ResponseStatus.SUCCESS) {
        const userAgent = typeof navigator === 'undefined' ? undefined : navigator.userAgent;
        const { accessToken } = response?.Data?.data ?? {};
        if (!accessToken) return;
        setCookie('accessToken', accessToken, {
          // 30일
          expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          domain: getDomainForCookie(),
        });
        await queryClient.invalidateQueries(AuthQueryKey.session());
        await queryClient.invalidateQueries(UserCompaniesQueryKey.default());
        const session = deserializeAccessToken(accessToken);
        const companyId = await getTargetCompany(session.userId);
        if (companyId == null) return;
        setCompanyId(companyId);
        const data = await getMetaData(companyId, session.userId, accessToken);

        const metaData = data?.Data;

        mixpanel.track(MIXPANEL_EVENT.USER.CREATE_WORKSPACE, {
          distinct_id: session.userId,
          browser: convert(userAgent, detectBrowser),
          platform: convert(userAgent, detectPlatform),
          tradesCount: metaData?.data?.company.numOfTrades,
          tradeStatus: metaData?.data?.company.tradeStatus,
          checkingAccountsCount: metaData?.data?.company.numOfCheckingAccounts,
          banksCount: metaData?.data?.company.numOfBanks,
          certificatesCount: metaData?.data?.company.numOfCertificates,
          dqBank: metaData?.data?.company.dataQualifications?.bank,
        });
      }
    },
  });
}

export function useCompaniesForCreateWorkspace(request: CompanyModel.FindWorkspaceCompanyRequest) {
  return useQuery({
    queryKey: CompaniesQueryKey.listForWorkspace(request),
    queryFn: () => getSearchCompany(request),
    enabled: !!request.businessRegNo,
  });
}

export function usePutRepresentativeInfo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: putRepresentativeInfo,
    onSuccess: async () => {
      await queryClient.invalidateQueries(USE_CEO_CREDIT_REQUEST_QUERY.root);
    },
  });
}

export function useResendCeoCreditAgreement() {
  return useMutation({
    mutationFn: resendCeoCreditAgreements,
  });
}
